import { useMemo, useState } from 'react';
import { Form } from 'antd';
import { Select, Divider, Row, Col, Upload } from 'antd5';
import { UploadOutlined } from '@ant-design/icons';
import { difference } from 'lodash';
import { usePapaParse } from 'react-papaparse';
import { Button, ModalActionButtons } from 'components/ui';
import { STEPS } from '../common';
import { LabelText, RequiredIndicator } from '../common.style';
import { DataField, PreviewDamage, SchemaOptions } from '../../types';

type UploadFormProps = {
  schemaOptions: SchemaOptions[];
  dataFields: DataField[];
  setPreviewDamage: (asset: PreviewDamage) => void;
  step: number;
  setStep: (step: number) => void;
  onClose: () => void;
  onClickPreview: () => void;
  getFieldsValue: (fields: string[] | true) => any;
  resetFields: () => void;
  fieldsHidden: boolean;
};

export const UploadForm: React.FunctionComponent<UploadFormProps> = ({
  schemaOptions,
  dataFields,
  setPreviewDamage,
  step,
  setStep,
  onClose,
  onClickPreview,
  getFieldsValue,
  resetFields,
  fieldsHidden,
}) => {
  const [headerOptions, setHeaderOptions] = useState<string[]>([]);
  const [usedHeaderOptions, setUsedHeaderOptions] = useState<string[]>([]);
  const [damageCount, setDamageCount] = useState<number>(0);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);
  const { readString } = usePapaParse();

  const handleSelectHeader = (): void => {
    setUsedHeaderOptions(
      Object.values(getFieldsValue(dataFields?.map(({ key }) => key)))
        .filter(Boolean)
        .map(String)
    );
  };

  const resetForm = (): void => {
    resetFields();
    setUsedHeaderOptions([]);
    setDamageCount(0);
    setStep(STEPS.upload);
  };

  const handleUpload = async (info: any) => {
    const { file } = info;
    const f: File = file;
    const csvText = await f.text();
    const rows = csvText.split('\n');

    const { data, meta } = readString(csvText, {
      header: true,
      preview: 1,
      skipEmptyLines: true,
    });
    const { fields } = meta;

    setHeaderOptions(fields);
    setUsedHeaderOptions(
      fields.filter((field: string) => dataFields.find(({ label }) => label === field))
    );

    setDamageCount(rows.filter((r: string) => r.trim().length).length - 1);
    setPreviewDamage(data[0]);
    setStep(STEPS.mapping);
    setSubmitDisabled(false);
  };

  const availableHeaderOptions = useMemo(
    () =>
      difference(headerOptions, usedHeaderOptions).map((h: string) => ({
        label: h,
        value: h,
      })),
    [headerOptions, usedHeaderOptions]
  );

  return (
    <>
      <Form.Item
        hidden={fieldsHidden}
        label="Damage Schema"
        key="damageSchemaId"
        name="damageSchemaId"
        rules={[
          {
            required: true,
            message: 'Please select damage schema',
            whitespace: true,
          },
        ]}
      >
        <Select
          placeholder="Select Asset Type"
          allowClear
          options={schemaOptions}
          disabled={schemaOptions.length < 2}
        />
      </Form.Item>

      {step === STEPS.upload && <p>Select file with damage information</p>}
      <Form.Item name="file" hidden={fieldsHidden}>
        <Upload
          accept={'.csv' /* TODO: xls? */}
          beforeUpload={() => false} // send false so it doesn't attempt to use antd upload.
          onChange={handleUpload}
          onRemove={resetForm}
        >
          {step === STEPS.upload && (
            <Button
              disabled={!getFieldsValue(['damageSchemaId']).damageSchemaId}
              icon={<UploadOutlined />}
            >
              Click to Upload
            </Button>
          )}
        </Upload>
      </Form.Item>

      {step === STEPS.mapping && !!headerOptions.length && (
        <>
          <Divider />
          <p>Select a source column for each attribute</p>
          {(dataFields ?? []).map(({ key, label, required }) => (
            <Row key={key}>
              <Col flex="1" />
              <Col flex="auto">
                <LabelText>
                  {required && <RequiredIndicator>* </RequiredIndicator>}
                  {label}:
                </LabelText>
              </Col>
              <Col flex="50%">
                <Form.Item
                  key={key}
                  name={key}
                  initialValue={headerOptions.find(option => option === label)}
                  rules={[
                    {
                      required,
                      message: `${label} cannot be empty`,
                      whitespace: true,
                    },
                  ]}
                >
                  <Select
                    placeholder="Select Source Column"
                    allowClear
                    options={availableHeaderOptions}
                    onChange={handleSelectHeader}
                  />
                </Form.Item>
              </Col>
              <Col flex="50px" />
            </Row>
          ))}
          <Divider />
          {damageCount > 0 && <b>Will create: {damageCount} damages</b>}
        </>
      )}

      {!fieldsHidden && (
        <ModalActionButtons>
          <Button
            onClick={() => {
              if (step === STEPS.upload) {
                onClose();
              } else {
                resetForm();
              }
            }}
          >
            {step === STEPS.upload ? 'Cancel' : 'Back'}
          </Button>
          {step !== STEPS.upload && (
            <Button type="primary" onClick={onClickPreview} disabled={submitDisabled}>
              Preview
            </Button>
          )}
        </ModalActionButtons>
      )}
    </>
  );
};
